import ApplicationModel, { attr } from '../ApplicationModel';
import EstimateTypeFilter, {
  type EstimateTypeFilterValue,
} from '../filters/EstimateTypeFilter';
import type ItemFilter from '../filters/ItemFilter';
import IterationFilter from '../filters/IterationFilter';
import RecordTypeFilter from '../filters/RecordTypeFilter';
import TeamFilter from '../filters/TeamFilter';
import type { UnitType } from '../lib/Estimate';
import { nameMap } from '../lib/nameMap';
import { model } from '../modelBuilder';
import Iteration from './Iteration';
import Project from './Project';
import { HasBookmarkFilters } from './concerns/HasBookmarkFilters';
import { type BookmarksIterationBurnupAttributes } from './generated/BookmarksIterationBurnupAttributes';
import moment from 'moment';

type BurnupFilters = {
  iterations: IterationFilter;
  recordType: RecordTypeFilter;
  estimationType: EstimateTypeFilter;
  teams: TeamFilter;
};

export type BurnupFilterScope = keyof BurnupFilters;

interface BookmarksIterationBurnup
  extends BookmarksIterationBurnupAttributes,
    HasBookmarkFilters<BurnupFilters> {
  filters: {
    iterations?: RecordId[];
    recordType?: string[];
    estimationType?: EstimateTypeFilterValue[];
    teams?: RecordId[];
  };
  local: {
    filterObjects?: Record<string, ItemFilter<string | RecordId>>;
  };
}
@model
@HasBookmarkFilters
class BookmarksIterationBurnup extends ApplicationModel {
  // Start auto-generated
  static typename: 'BookmarksIterationBurnup' = 'BookmarksIterationBurnup';
  get typename(): 'BookmarksIterationBurnup' {
    return 'BookmarksIterationBurnup';
  }

  static internalTypename: 'Bookmarks::IterationBurnup' =
    'Bookmarks::IterationBurnup';
  get internalTypename(): 'Bookmarks::IterationBurnup' {
    return 'Bookmarks::IterationBurnup';
  }

  static bookmarkName = 'Bookmarks::IterationBurnup';

  static inputType = 'IterationBurnupAttributes!';

  static fields = {
    filters: attr({ writable: true, readable: true, json: true }),
    id: attr({ readable: true }),
    projectId: attr({ readable: true }),
    project: attr.belongsTo({ readable: true, typename: 'Project' }),
    recentIterationsBurnupStats: attr.belongsTo({
      readable: true,
      typename: 'BookmarksIterationBurnupStats',
    }),
  };
  // End auto-generated

  /**
   * Load all iterations - custom implementation for team lines
   */
  async availableIterations(): Promise<Iteration[]> {
    const project = this.project || this.attributes.project;

    // If this is a team line, fetch iterations from selected teams
    if (project?.isProductLine) {
      const teams = await this.selectedTeams();
      if (teams.length > 0) {
        const teamIds = teams.map(([, id]) => id);

        // Fetch iterations for each team separately and combine results
        const allIterations = await Promise.all(
          teamIds.map(async teamId => {
            return Iteration.select(
              'id',
              'name',
              'status',
              'duration',
              'startDate',
              'endDate',
              'createdAt'
            )
              .merge({ originalEstimate: ['units', 'value'] })
              .where({ projectId: teamId, status: [20, 30] })
              .order({ startDate: 'DESC' })
              .all();
          })
        );

        // Flatten the array of ModelCollections into a single array of iterations
        const iterations = allIterations.flatMap(collection =>
          Array.from(collection)
        );

        return iterations.filter(i =>
          moment(i.createdAt).isAfter(Iteration.BURNDOWN_AVAILABLE_DATE)
        );
      }
      return [];
    }

    // Single team mode - use standard implementation
    const collection = await Iteration.select(
      'id',
      'name',
      'status',
      'duration',
      'startDate',
      'endDate',
      'createdAt'
    )
      .merge({ originalEstimate: ['units', 'value'] })
      .where({ projectId: this.projectId, status: [20, 30] })
      .order({ startDate: 'DESC' })
      .all();

    const iterations = Array.from(collection);
    return iterations.filter(i =>
      moment(i.createdAt).isAfter(Iteration.BURNDOWN_AVAILABLE_DATE)
    );
  }

  /**
   * Get a specific iteration by ID
   */
  async getIteration({
    iterationId,
  }: {
    iterationId?: string;
  }): Promise<Iteration | undefined> {
    const iterations = await this.availableIterations();
    if (!iterationId && iterations.length > 0) {
      return iterations[0];
    }
    return iterations.find(i => i.id === iterationId);
  }

  async createFilters(): Promise<BurnupFilters> {
    const [iterations, teams] = await Promise.all([
      this.availableIterations(),
      this.selectedTeams(),
    ]);

    const iterationFilter = new IterationFilter(
      this.filters.iterations || [],
      (iterations ?? []).map(nameMap)
    );

    let defaultEstimationType: EstimateTypeFilterValue = 'count';

    const iteration = await this.iteration(this.filters.iterations?.[0]);

    if (iteration?.defaultEstimationType === 'original') {
      defaultEstimationType = 'originalEstimate';
    }

    return {
      iterations: iterationFilter,
      recordType: new RecordTypeFilter({
        initialItems:
          typeof this.filters.recordType === 'string'
            ? // Convert legacy recordType shape ^
              [this.filters.recordType]
            : Array.isArray(this.filters.recordType)
            ? this.filters.recordType
            : [],
        recordTypes: ['Feature', 'Requirement'],
      }),
      estimationType: new EstimateTypeFilter({
        initialItems: this.filters.estimationType || [defaultEstimationType],
        capacityUnits: iteration?.originalEstimate?.units,
      }),
      teams: new TeamFilter(
        teams.map(([, id]) => id),
        teams
      ),
    };
  }

  /**
   * Fetch events for the selected iteration
   *
   * @param filters Any filters to apply to the raw data fetch.
   */
  async recordEvents(filters: Record<string, any> = {}) {
    const iteration = await this.iteration();
    return iteration?.eventsRaw(filters);
  }

  /**
   * Load the selected iteration using the available iterations
   */
  async iteration(iterationId?: string): Promise<Iteration | null> {
    const iterations = await this.availableIterations();
    if (!iterations) return null;

    iterationId = iterationId ?? this.getFilter('iterations')?.single();
    if (!iterationId) return iterations[0];

    return iterations.find(i => i.id === iterationId) || iterations[0];
  }

  /**
   * Load the selected unit
   */
  async units(): Promise<UnitType> {
    const units = this.getFilter('estimationType')?.single();

    if (units === 'count') {
      return 'COUNT';
    } else {
      const iteration = await this.iteration();
      return iteration?.originalEstimate?.units ?? 'POINTS';
    }
  }

  async selectedTeams(): Promise<[string, RecordId][]> {
    const project = this.project || this.attributes.project;
    if (!project?.isProductLine) {
      return [];
    }
    const childIds = project.children.map(c => c.id);
    const teamIds = this.filters.teams?.length ? this.filters.teams : childIds;

    if (teamIds.length > 0) {
      try {
        const teams = await Project.select(['id', 'name'])
          .where({ id: teamIds })
          .all();
        return teams.map(t => [t.name, t.id]);
      } catch (error) {
        console.error('selectedTeams - error fetching teams:', error);
        return [];
      }
    }
    return [];
  }

  /**
   * Get open iterations from each selected team (for team line mode)
   */
  async openIterationsFromTeams(): Promise<Iteration[]> {
    const project = this.project || this.attributes.project;
    if (!project?.isProductLine) {
      return [];
    }

    const teams = await this.selectedTeams();
    if (teams.length === 0) {
      return [];
    }

    const teamIds = teams.map(([, id]) => id);

    // Fetch the currently open iteration (status 20 = started) from each team
    const allIterations = await Promise.all(
      teamIds.map(async teamId => {
        return Iteration.select(
          'id',
          'name',
          'status',
          'duration',
          'startDate',
          'endDate',
          'createdAt'
        )
          .merge({ originalEstimate: ['units', 'value'] })
          .where({ projectId: teamId, status: 20 }) // Only started iterations
          .order({ startDate: 'DESC' })
          .all();
      })
    );

    // Flatten and get the first (most recent) open iteration from each team
    return allIterations
      .flatMap(collection => Array.from(collection))
      .filter(i =>
        moment(i.createdAt).isAfter(Iteration.BURNDOWN_AVAILABLE_DATE)
      );
  }

  canFilter(_scope) {
    return true;
  }
}

export default BookmarksIterationBurnup;
